import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import { requestRecoveryCode, resetRecoveryPassword } from "./authApi";
import "./auth.css";
import { normalizeLang } from "../i18n";
import { resolvePostAuthTarget } from "./postAuthTarget";
import LegalFooterLinks from "../components/LegalFooterLinks";

function useNextPath() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const lang = normalizeLang(params.get("lang") || "he");
  return resolvePostAuthTarget(params.get("next"), lang);
}

export default function AuthLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = useNextPath();
  const { login, loginWithCode } = useAuth();
  const { t, i18n } = useTranslation();
  const urlLangExplicit = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("lang");
    if (raw == null || String(raw).trim() === "") return null;
    return normalizeLang(raw);
  }, [location.search]);
  const langForLinks = urlLangExplicit ?? normalizeLang(i18n.language);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [mode, setMode] = useState("password");
  const [resetMode, setResetMode] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [devCode, setDevCode] = useState("");
  const appliedUrlReset = useRef(false);
  const showDevCode =
    (process.env.REACT_APP_SHOW_DEV_CODE || "").toLowerCase() === "true" ||
    (typeof window !== "undefined" &&
      ["localhost", "127.0.0.1", "notbrokenforever.com", "www.notbrokenforever.com"].includes(
        window.location.hostname
      ));

  useEffect(() => {
    if (urlLangExplicit && i18n.language !== urlLangExplicit) {
      i18n.changeLanguage(urlLangExplicit);
    }
  }, [urlLangExplicit, i18n]);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const preEmail = (q.get("email") || "").trim();
    if (preEmail) {
      setEmail((prev) => (String(prev || "").trim() ? prev : preEmail));
    }
    if (appliedUrlReset.current) return;
    if (q.get("reset") !== "1" && q.get("forgot") !== "1") return;
    appliedUrlReset.current = true;
    setMode("code");
    setResetMode(true);
    setCodeSent(false);
    setError("");
  }, [location.search]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "password" && !resetMode) {
        await login({ email, password });
      } else if (resetMode) {
        await resetRecoveryPassword({ email, code, newPassword });
      } else {
        await loginWithCode({ email, code });
      }
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err.message || t("auth.loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    setError("");
    setLoading(true);
    setDevCode("");
    try {
      const res = await requestRecoveryCode({ email });
      if (showDevCode && res.devCode) setDevCode(res.devCode);
      setCodeSent(true);
    } catch (err) {
      setError(err.message || t("auth.sendFailed"));
    } finally {
      setLoading(false);
    }
  };

  const isResetSubmitDisabled =
    loading ||
    (resetMode && (!codeSent || !email || !code || newPassword.length < 8));
  const hasRecoveryCode = String(code || "").trim().length > 0;

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={handleSubmit}>
        <h2>{t("auth.title")}</h2>
        <div className="auth-toggle">
          <button
            type="button"
            className={mode === "password" ? "active" : ""}
            onClick={() => {
              setMode("password");
              setResetMode(false);
              setError("");
            }}
          >
            {t("auth.passwordTab")}
          </button>
          <button
            type="button"
            className={mode === "code" ? "active" : ""}
            onClick={() => {
              setMode("code");
              setError("");
            }}
          >
            {t("auth.emailTab")}
          </button>
        </div>
        {error && <div className="auth-error">{error}</div>}
        <label>
          {t("auth.email")}
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        {mode === "password" ? (
          <>
            <label>
              {t("auth.password")}
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>
            <button
              type="button"
              className="auth-link-btn"
              onClick={() => {
                setMode("code");
                setResetMode(true);
                setCode("");
                setCodeSent(false);
                setError("");
              }}
            >
              {t("auth.forgotPassword")}
            </button>
          </>
        ) : (
          <>
            <div className="auth-code-row">
              <button type="button" onClick={handleSendCode} disabled={!email || loading}>
                {loading ? t("auth.sending") : t("auth.sendCode")}
              </button>
              {codeSent && <span className="auth-muted">{t("auth.codeSent")}</span>}
            </div>
            {showDevCode && devCode && (
              <div className="auth-muted">
                {t("auth.devCode")}: {devCode}
              </div>
            )}
            {(!resetMode || codeSent) && (
              <>
                <label>
                  {t("auth.code")}
                  <input
                    type="text"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    required
                  />
                </label>
                {resetMode && hasRecoveryCode && (
                  <>
                    <label>
                      {t("auth.newPassword")}
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        minLength={8}
                        required
                      />
                    </label>
                    <button
                      type="button"
                      className="auth-link-btn"
                      onClick={() => {
                        setResetMode(false);
                        setMode("password");
                        setError("");
                      }}
                    >
                      {t("auth.backToLogin")}
                    </button>
                  </>
                )}
              </>
            )}
          </>
        )}
        <button type="submit" disabled={isResetSubmitDisabled}>
          {loading
            ? t("auth.working")
            : resetMode
              ? t("auth.resetPassword")
              : t("auth.login")}
        </button>
        <p className="auth-hint">
          {t("auth.noAccount")}{" "}
          <Link
            to={`/auth/register?next=${encodeURIComponent(nextPath)}&lang=${encodeURIComponent(langForLinks)}`}
          >
            {t("auth.createOne")}
          </Link>
        </p>
        <LegalFooterLinks />
      </form>
    </div>
  );
}
